import React, { useEffect, useState } from 'react';
import { ArrowLeft, Calendar, Check, X, Package, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSellerOrders, updateOrderStatus } from '../../services/orderService';
import { Order } from '../../types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const SellerOrders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'shipped'>('pending');

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getSellerOrders(user.id);
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-24">
      {/* Header */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/seller/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <h1 className="text-xl font-bold text-gray-800">Order Management</h1>
      </div>

      <div className="p-4">
        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
          {['pending', 'accepted', 'shipped', 'all'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`px-4 py-2 rounded-xl whitespace-nowrap font-medium transition-all ${
                filter === status
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {status !== 'all' && (
                <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
                  {orders.filter(o => o.status === status).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {filteredOrders.length === 0 ? (
          <div className="text-center py-16">
            <Package size={64} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No orders found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order, index) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg"
              >
                {/* Order Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-xs text-gray-500">{format(order.createdAt, 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    order.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {order.status.toUpperCase()}
                  </span>
                </div>

                {/* Artwork & Buyer Info */}
                <div className="flex gap-4 mb-4">
                  <img
                    src={order.artworkImage}
                    alt={order.artworkTitle}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{order.artworkTitle}</h3>
                    <p className="text-sm text-gray-600 mb-1">Buyer: {order.buyerName}</p>
                    <p className="text-sm text-gray-500">Qty: {order.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">₹{order.totalAmount.toLocaleString()}</p>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <Package size={18} className="text-blue-600" />
                    Shipping Address
                  </p>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p className="font-medium">{order.shippingAddress.name}</p>
                    <p>{order.shippingAddress.phone}</p>
                    <p>{order.shippingAddress.street}</p>
                    <p>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</p>
                    {order.shippingAddress.landmark && (
                      <p className="text-gray-500">Landmark: {order.shippingAddress.landmark}</p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {order.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/seller/orders/${order.id}/accept`)}
                      className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <Check size={20} />
                      Accept Order
                    </button>
                    <button
  onClick={async () => {
    if (window.confirm('Are you sure you want to reject this order?')) {
      await updateOrderStatus(order.id, 'rejected', undefined, order.buyerId);
      loadOrders();
    }
  }}
  className="px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
>
  <X size={20} />
</button>
                  </div>
                )}

                {order.status === 'accepted' && (
  <button
    onClick={async () => {
      await updateOrderStatus(order.id, 'shipped', undefined, order.buyerId);
      loadOrders();
    }}
    className="w-full py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
  >
    <Package size={20} />
    Mark as Shipped
  </button>
)}

               {order.status === 'shipped' && (
  <button
    onClick={async () => {
      await updateOrderStatus(order.id, 'delivered', undefined, order.buyerId);
      loadOrders();
    }}
    className="w-full py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
  >
    <Check size={20} />
    Mark as Delivered
  </button>
)}

                <button
                  onClick={() => navigate(`/seller/orders/${order.id}`)}
                  className="w-full mt-2 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye size={20} />
                  View Details
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerOrders;